import { useEffect, useRef, useState } from "react";

import {
  BaseInput,
  CustomDatePicker,
  Icon,
  RadioButtons,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import { formatDate } from "@smart/itops-utils-basic";

type SharedFormsSelectProps = {
  value?: string;
  onChange: (value: string) => void;
  error?: boolean;
  message?: string;
  disabled?: boolean;
};

const toDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fromDateValue = (value?: string) =>
  value ? new Date(`${value}T00:00:00`) : undefined;

export const SharedFormsSelect = ({
  value,
  onChange,
  error,
  message,
  disabled,
}: SharedFormsSelectProps) => {
  const [open, setOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState(
    value ? "effectiveFrom" : "all",
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedMode(value ? "effectiveFrom" : "all");
  }, [value]);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  return (
    <div>
      <div className="mb-2 flex items-center gap-1">
        <span className="text-ui font-semibold">Shared from</span>
        <Tooltip
          className="ml-2 flex items-center"
          text="Only includes forms marked as Shared, on or after this date"
          variant="paragraphy"
          placement="bottom"
          offsetBy={7}
        >
          <Icon
            className="h-6 w-6 text-neutral-600"
            name="regularCircleInfo"
            dataTestId="reminder-shared-from-label-tooltip"
          />
        </Tooltip>
      </div>
      <div className="relative" ref={containerRef}>
        <BaseInput
          value={value ? formatDate(fromDateValue(value)) : "All shared forms"}
          readOnly
          disabled={disabled}
          error={error}
          rightButtonIcon="regularAngleDown"
          onClick={() => !disabled && setOpen((current) => !current)}
          onRightButtonClick={() => setOpen((current) => !current)}
          dataTestId="reminder-shared-from"
        />
        {open && (
          <div className="absolute left-0 right-0 top-[calc(100%+0.4rem)] z-modal min-w-[28rem] rounded bg-white p-[1.6rem] shadow-dropdownMenu">
            <RadioButtons
              name="reminder-shared-from-mode"
              direction="column"
              withLabelWrapper={false}
              value={selectedMode}
              onChange={(nextMode) => {
                setSelectedMode(nextMode);
                if (nextMode === "all") {
                  onChange("");
                  setOpen(false);
                }
              }}
              options={[
                { value: "all", label: "All shared forms" },
                {
                  value: "effectiveFrom",
                  label: "Forms shared on or after",
                  labelOverride: (
                    <div className="flex min-w-0 flex-1 flex-col gap-small ml-2">
                      <span className="text-ui">Forms shared on or after</span>
                      <CustomDatePicker
                        selectedDate={fromDateValue(value)}
                        onClick={() => setSelectedMode("effectiveFrom")}
                        onDateChange={(date) => {
                          onChange(toDateValue(date));
                          setSelectedMode("effectiveFrom");
                          setOpen(false);
                        }}
                        dataTestId="reminder-shared-from-date"
                        unrestricted
                        disabled={disabled}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
      {message && (
        <div
          className={
            error
              ? "mt-2 text-caption font-semibold text-red-550"
              : "mt-2 text-caption text-neutral-600"
          }
        >
          {message}
        </div>
      )}
    </div>
  );
};
